import { Box } from "@mui/material";
import {
  useHuntersListQuery,
  type HuntersListParams,
} from "Api/Queries/User/useHuntersListQuery";
import { HuntersTable } from "Components/Hunter/HuntersOverview/HuntersTable";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useServerTable } from "Hooks/useServerTable";
import type { HunterListDto } from "Infrastructure/Api/Api";
import { useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { asString, asStringArray, asDateISOString } from "Utils/FilterUtils";

const RESOURCES = Resources.Hunter.HuntersOverview;

const mapFiltersToParams = (
  filters: Record<string, unknown>,
): Partial<HuntersListParams> => ({
  prerequisites: asString(filters.prerequisites),
  login: asString(filters.login),
  firstName: asString(filters["party.firstName"]),
  lastName: asString(filters["party.lastName"]),
  companyName: asString(filters["party.companyName"]),
  affiliatedTo: asString(filters.affiliatedTo),
  dateCreatedFrom: asDateISOString(filters.dateCreated),
  statuses: asStringArray(filters.status),
});

export const HuntersPageContent = () => {
  const { t } = useResource();

  const { queryParams, tableProps } = useServerTable<HunterListDto>({
    defaultSorting: [{ id: "dateCreated", desc: true }],
  });

  const apiParams = useMemo(
    (): HuntersListParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: queryParams.sortBy,
      sortDirection: queryParams.sortDirection,
      ...mapFiltersToParams(queryParams.filters),
    }),
    [queryParams],
  );

  const { data, isLoading, isRefetching } = useHuntersListQuery(apiParams);

  if (!isLoading && !data) {
    return <ErrorText>{t(RESOURCES.LoadingError)}</ErrorText>;
  }

  return (
    <Box display="grid" alignItems="start">
      <HuntersTable
        data={data?.items || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        rowCount={data?.totalCount ?? 0}
        tableProps={tableProps}
      />
    </Box>
  );
};
